import * as vp from 'vopay';

import type { ApiPartnerPermissions } from 'vopay';
import type { PortalPartnerLoginResponse } from './useApi/usePostPortalPartnerLogin';

const { useSessionStorage, useLocalStorage } = vp.useStorage;

const useStorage = () => {
  const handlers = {
    useLoginUserNameStorage: wrapLocalStorage<string>(vp.LS_USER_LOGIN_USER_NAME),
    usePartnerTypeStorage: wrapSessionStorage<vp.PartnerType>(vp.LS_USER_PARTNER_TYPE),
    usePartnerTransactionsStorage: () =>
      useSessionStorage<vp.AccountTransactionsMap>(vp.LS_PARTNER_TRANSACTIONS_RESPONSE),
    useAccountIdStorage: wrapSessionStorage<string>(vp.LS_USER_ACCOUNT_ID),
    useJWTStorage: wrapSessionStorage<string>(vp.LS_USER_JWT),
    useAccountDomainStorage: wrapSessionStorage<string>(vp.LS_USER_DOMAIN),
    useIsInsideSalesStorage: () => useSessionStorage<boolean>(vp.LS_IS_INSIDE_SALES),
    useAccountUsername: wrapSessionStorage<string>(vp.LS_USER_NAME),
    useAccountLoginUsername: wrapSessionStorage<string>(vp.LS_USER_LOGIN_NAME),
    usePartnerPermissions: () => useSessionStorage<ApiPartnerPermissions>(vp.LS_USER_PERMISSIONS),
    useSignerCreation: () => useSessionStorage<string>(vp.LS_SIGNER_CREATION),
    useAccountToken: () => useSessionStorage<string>(vp.LS_ACCOUNT_TOKEN),
    useUserRole: () => useSessionStorage<vp.PartnerRole>(vp.LS_USER_ROLE),
    useSuccessStorage: () => useLocalStorage<boolean>(vp.LS_USER_LOGIN_SUCCESS),
    useMFAEnabledStorage: () => useLocalStorage<boolean>(vp.LS_USER_MFA_ENABLED),
  };

  const [partnerType, storePartnerType, clearPartnerType] = handlers.usePartnerTypeStorage();
  const [accountId, storeAccountId, clearAccountId] = handlers.useAccountIdStorage();
  const [username, storeUsername, clearUsername] = handlers.useAccountUsername();
  const [loginUsername, storeLoginUsername, clearLoginUsername] = handlers.useAccountLoginUsername();
  const [permissions, storePermissions, clearPermissions] = handlers.usePartnerPermissions();
  const [domain, storeDomain, clearDomain] = handlers.useAccountDomainStorage();
  const [isInsideSales, storeIsInsideSales] = handlers.useIsInsideSalesStorage();
  const [signerCreation, storeSignerCreation, clearSignerCreation] = handlers.useSignerCreation();
  const [accountToken, storeAccountToken, clearAccountToken] = handlers.useAccountToken();
  const [userRole, storeUserRole] = handlers.useUserRole();
  const [success, storeSuccess, clearSuccess] = handlers.useSuccessStorage();
  const [MFAEnabled, storeMFAEnabled, clearMFAEnabled] = handlers.useMFAEnabledStorage();
  const [LoginUserName, storeLoginUserName, clearLoginUserName] = handlers.useLoginUserNameStorage();
  const [JWT, storeJWT, clearJWT] = handlers.useJWTStorage();
  const [partnerTransactions, storePartnerTransactions, clearPartnerTransactions] =
    handlers.usePartnerTransactionsStorage();

  return {
    login: {
      getPartnerLogin: () => ({
        partnerType,
        accountId,
        username,
        loginUsername,
        domain,
        userRole,
        isInsideSales,
        success,
        MFAEnabled,
        LoginUserName,
        JWT,
      }),
      storePartnerLogin: (response: PortalPartnerLoginResponse, LoginUsername: string) => {
        if (LoginUsername !== '') {
          storeLoginUserName(LoginUsername);
        }
        storePartnerType(response.partnerType);
        storeAccountId(response.accountId);
        storeUsername(response.name);
        storeLoginUsername(LoginUsername);
        storeDomain(response.domain);
        storeIsInsideSales(response.isInsideSales);
        storeUserRole(response.role);
        storeSuccess(response.success);
        storeMFAEnabled(response.MFAEnabled);
      },
      clearPartnerLogin: () => {
        clearPartnerType();
        clearAccountId();
        clearJWT();
        clearUsername();
        clearLoginUsername();
        clearDomain();
        clearSuccess();
        clearMFAEnabled();
        clearLoginUserName();
      },
      storeLoginValues: (response: PortalPartnerLoginResponse) => {
        storeJWT(response.JWT);
      },
      storeLoginUsername: (LoginUsername: string) => {
        if (LoginUsername !== '') {
          storeLoginUserName(LoginUsername);
        }
      },
    },
    permissions: {
      getPartnerPermissions: () => permissions,
      storePermissions: (response: PortalPartnerLoginResponse) => storePermissions(response.permissions),
      clearPermissions,
    },
    signerCreation: {
      getSignerCreation: () => signerCreation,
      storeSignerCreation: (response: string) => storeSignerCreation(response),
      clearSignerCreation,
    },
    accountToken: {
      getAccountToken: () => accountToken,
      storeAccountToken: (response: string) => storeAccountToken(response),
      clearAccountToken,
    },

    partnerTransactions: {
      getPartnerTransactions: () => partnerTransactions,
      storePartnerTransactions: (response: vp.AccountTransactionsMap) => storePartnerTransactions(response),
      clearPartnerTransactions: clearPartnerTransactions,
    },
  };
};

function wrapSessionStorage<T extends string>(key: string): () => [T | '', (input: T) => void, () => void] {
  return () => {
    const [value, setValue, clearValue] = useSessionStorage<T | ''>(key, '');
    return [value || '', (v: T) => setValue(v), () => clearValue()];
  };
}

function wrapLocalStorage<T extends string>(key: string): () => [T | '', (input: T) => void, () => void] {
  return () => {
    const [value, setValue, clearValue] = useLocalStorage<T | ''>(key, '');
    return [value || '', (v: T) => setValue(v), () => clearValue()];
  };
}

export default useStorage;
